import { useState, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { faListCheck } from "@fortawesome/pro-solid-svg-icons/faListCheck";
import { faHeart } from "@fortawesome/pro-solid-svg-icons/faHeart";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons/faChevronDown";
import { faChevronUp } from "@fortawesome/pro-solid-svg-icons/faChevronUp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapPin } from "@fortawesome/pro-solid-svg-icons/faMapPin";
import {
  Button,
  toast,
} from "@wondermaps/ui";
import { VisitorDrawer } from "./VisitorDrawer";
import { SheetHeader } from "./SheetHeader";
import { useAvailableCuratedLists } from "../../hooks/visitor/useAvailableCuratedLists";
import { findIconByName } from "../../data/fontawesome-icons";
import { api } from "../../lib/api";
import { analytics } from "../../lib/analytics";
import type { POI } from "../MapCanvas";

interface ListItem {
  poiId: string;
  note: string | null;
}

interface ExpandedListData {
  items: ListItem[];
}

interface CuratedListBrowserSheetProps {
  isOpen: boolean;
  onClose: () => void;
  orgSlug: string;
  mapSlug: string;
  pois: POI[];
  bookmarkedIds: Set<string>;
  toggleBookmark: (poiId: string) => void;
  onLoadPOIs: (poiIds: string[]) => void;
  onSelectPOI: (poi: POI) => void;
  initialExpandSlug?: string;
}

export function CuratedListBrowserSheet({
  isOpen,
  onClose,
  orgSlug,
  mapSlug,
  pois,
  bookmarkedIds,
  toggleBookmark,
  onLoadPOIs,
  onSelectPOI,
  initialExpandSlug,
}: CuratedListBrowserSheetProps) {
  const { t } = useTranslation("map");
  const { data: lists } = useAvailableCuratedLists(orgSlug, mapSlug, isOpen);
  const [expandedSlug, setExpandedSlug] = useState<string | null>(null);
  const [expandedData, setExpandedData] = useState<Record<string, ExpandedListData>>({});
  const [loadingSlug, setLoadingSlug] = useState<string | null>(null);
  const listCardRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Index POIs by ID for fast lookup
  const poisById = useMemo(() => {
    const map = new Map<string, POI>();
    for (const p of pois) map.set(p.id, p);
    return map;
  }, [pois]);

  // Auto-expand a specific list when opened via deep link
  const autoExpandedRef = useRef(false);
  useEffect(() => {
    if (!initialExpandSlug || !lists?.length || autoExpandedRef.current) return;
    const target = lists.find((l) => l.slug === initialExpandSlug);
    if (!target) return;
    autoExpandedRef.current = true;
    handleToggleExpand(target.slug, target.name);
  }, [lists, initialExpandSlug]);

  // Scroll to auto-expanded list after it loads
  useEffect(() => {
    if (!expandedSlug || !initialExpandSlug || expandedSlug !== initialExpandSlug) return;
    // Wait for DOM update after expand
    const rafId = requestAnimationFrame(() => {
      listCardRefs.current.get(expandedSlug)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(rafId);
  }, [expandedSlug, initialExpandSlug]);

  const handleToggleExpand = async (listSlug: string, listName: string) => {
    // Collapse if already expanded
    if (expandedSlug === listSlug) {
      setExpandedSlug(null);
      return;
    }

    // If already fetched, just expand
    if (expandedData[listSlug]) {
      setExpandedSlug(listSlug);
      return;
    }

    // Lazy-fetch list detail
    setLoadingSlug(listSlug);
    try {
      const res = await api.maps.lists[":orgSlug"][":mapSlug"][":listSlug"].$get({
        param: { orgSlug, mapSlug, listSlug },
      });
      if (!res.ok) throw new Error("fetch failed");
      const data = await res.json();
      const items = data.list.items.map((i) => ({ poiId: i.poiId, note: i.note ?? null }));
      setExpandedData((prev) => ({ ...prev, [listSlug]: { items } }));
      setExpandedSlug(listSlug);

      analytics.curatedListPreviewed({
        list_slug: listSlug,
        list_name: listName,
        poi_count: items.length,
      });
    } catch {
      toast({ title: t("curatedList.loadFailed", { defaultValue: "Failed to load list" }), variant: "destructive" });
    } finally {
      setLoadingSlug(null);
    }
  };

  const handleLoadAll = async (listSlug: string, listName: string) => {
    // Use cached data if we already fetched it via preview
    const cached = expandedData[listSlug];
    if (cached) {
      const poiIds = cached.items.map((i) => i.poiId);
      onLoadPOIs(poiIds);
      analytics.curatedListLoaded({
        list_slug: listSlug,
        list_name: listName,
        poi_count: poiIds.length,
        source: "browse",
      });
      toast({ title: t("curatedList.loaded", { name: listName, count: poiIds.length }) });
      onClose();
      return;
    }

    // Otherwise fetch fresh
    try {
      const res = await api.maps.lists[":orgSlug"][":mapSlug"][":listSlug"].$get({
        param: { orgSlug, mapSlug, listSlug },
      });
      if (!res.ok) throw new Error("load failed");
      const data = await res.json();
      const poiIds = data.list.items.map((i) => i.poiId);
      onLoadPOIs(poiIds);
      analytics.curatedListLoaded({
        list_slug: listSlug,
        list_name: listName,
        poi_count: poiIds.length,
        source: "browse",
      });
      toast({ title: t("curatedList.loaded", { name: listName, count: poiIds.length }) });
      onClose();
    } catch {
      toast({ title: t("curatedList.loadFailed", { defaultValue: "Failed to load list" }), variant: "destructive" });
    }
  };

  const isExpanded = (slug: string) => expandedSlug === slug;

  return (
    <VisitorDrawer open={isOpen} onOpenChange={(open) => !open && onClose()} title={t("menu.browseLists")}>
      <SheetHeader
        title={t("menu.browseLists")}
        icon={<FontAwesomeIcon icon={faListCheck} className="h-5 w-5" />}
        onClose={onClose}
      />

      <div className="overflow-y-auto py-4 space-y-3">
        {!lists?.length ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            {t("curatedList.noLists", { defaultValue: "No lists available" })}
          </p>
        ) : (
          lists.map((list) => (
            <div
              key={list.id}
              className="border rounded-lg overflow-hidden"
              ref={(el) => { if (el) listCardRefs.current.set(list.slug, el); }}
            >
              {/* List card header */}
              <div className="flex items-start gap-3 p-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{list.name}</div>
                  {list.description && (
                    <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                      {list.description}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {t("curatedList.stops", { count: list.itemCount, defaultValue: "{{count}} stops" })}
                  </p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleToggleExpand(list.slug, list.name)}
                    disabled={loadingSlug === list.slug}
                  >
                    {loadingSlug === list.slug ? (
                      <span className="animate-pulse">...</span>
                    ) : isExpanded(list.slug) ? (
                      <>{t("curatedList.hide")} <FontAwesomeIcon icon={faChevronUp} className="ml-1 h-3.5 w-3.5" /></>
                    ) : (
                      <>{t("curatedList.view")} <FontAwesomeIcon icon={faChevronDown} className="ml-1 h-3.5 w-3.5" /></>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleLoadAll(list.slug, list.name)}
                  >
                    {t("curatedList.loadAll")}
                  </Button>
                </div>
              </div>

              {/* Expanded POI list */}
              {isExpanded(list.slug) && expandedData[list.slug] && (
                <div className="border-t bg-muted/30">
                  {expandedData[list.slug].items.map((item) => {
                    const poi = poisById.get(item.poiId);
                    if (!poi) return null;

                    const iconEntry = poi.category?.icon
                      ? findIconByName(poi.category.icon)
                      : null;
                    const saved = bookmarkedIds.has(poi.id);

                    return (
                      <div
                        key={item.poiId}
                        className="flex items-center gap-3 px-3 py-2 border-b last:border-b-0"
                      >
                        {/* Tappable icon + name area → opens POI detail */}
                        <button
                          type="button"
                          className="flex items-center gap-3 flex-1 min-w-0 text-left"
                          onClick={() => onSelectPOI(poi)}
                        >
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                            style={{ backgroundColor: poi.category?.color ?? "#888" }}
                          >
                            <FontAwesomeIcon
                              icon={iconEntry?.icon ?? faMapPin}
                              className="w-3.5 h-3.5 text-white"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{poi.name}</div>
                            {item.note && (
                              <p className="text-xs text-muted-foreground truncate italic">
                                {item.note}
                              </p>
                            )}
                          </div>
                        </button>

                        {/* Bookmark toggle */}
                        <button
                          type="button"
                          onClick={() => toggleBookmark(poi.id)}
                          className="min-w-[36px] min-h-[36px] flex items-center justify-center shrink-0 active:scale-90 transition-transform"
                          aria-label={saved ? t("pois:removeSaved", { name: poi.name }) : t("pois:savePoi", { name: poi.name })}
                          aria-pressed={saved}
                        >
                          <FontAwesomeIcon icon={faHeart}
                            className={
                              saved
                                ? "w-4.5 h-4.5 fill-red-500 text-red-500"
                                : "w-4.5 h-4.5 text-muted-foreground"
                            }
                          />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </VisitorDrawer>
  );
}
